import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, ImagePlus, Search, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { normalizeListMeta } from "@/components/data-grid/list-meta";
import { useListQueryState } from "@/components/data-grid/use-list-query-state";
import { ListWorkspace } from "@/components/workspace/list-workspace";
import { SummaryStrip } from "@/components/workspace/summary-strip";
import { Button, Input, Select } from "@/components/ui/controls";
import { EmptyState, ErrorState, Skeleton } from "@/components/ui/states";
import { toMediaAsset } from "@/lib/adapters/operations";
import { ApiError, apiRequest } from "@/lib/api/client";
import { formatUploadBytes, mediaUploadResultSchema, type MediaUploadResult } from "@/lib/media-upload";
import { mediaSchema } from "@/lib/schemas/operations";
import { useOptionalToast } from "@/components/ui/toast-context";
import styles from "../operations-support.module.css";

export default function MediaPage() {
  const input = useRef<HTMLInputElement>(null);
  const client = useQueryClient();
  const notify = useOptionalToast();
  const list = useListQueryState({ filters: { kind: "" } as const });
  const [feedback, setFeedback] = useState<{ kind: "error" | "success"; message: string } | null>(null);
  const query = useQuery({
    queryKey: ["media", list.search, list.filters.kind, list.page, list.pageSize],
    queryFn: async ({ signal }) => {
      const queryString = new URLSearchParams({
        page: String(list.page),
        page_size: String(list.pageSize),
      });
      if (list.search) queryString.set("search", list.search);
      if (list.filters.kind) queryString.set("kind", list.filters.kind);
      const result = await apiRequest(`media/?${queryString}`, z.array(mediaSchema), { signal });
      const rows = result.data.map(toMediaAsset);
      return { rows, meta: normalizeListMeta(result.meta, { page: list.page, pageSize: list.pageSize, rowCount: rows.length }) };
    },
  });
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const results: MediaUploadResult[] = [];
      for (const file of files) {
        const body = new FormData();
        body.append("file", file);
        body.append("locale", "tr");
        const result = await apiRequest("media/", mediaUploadResultSchema, { method: "POST", body });
        results.push(result.data);
      }
      return results;
    },
    onSuccess: (results) => {
      const optimized = results.filter((result) => result.optimized);
      const savedBytes = optimized.reduce((total, result) => total + result.saved_bytes, 0);
      const description = optimized.length
        ? `${optimized.length} görsel optimize edildi · ${formatUploadBytes(savedBytes)} alan kazanıldı.`
        : `${results.length} dosya medya kütüphanesine eklendi.`;
      setFeedback({ kind: "success", message: description });
      notify({ title: "Medya yükleme tamamlandı", description, tone: "success" });
      void client.invalidateQueries({ queryKey: ["media"] });
    },
    onError: (reason) => {
      const message = reason instanceof ApiError ? reason.message : "Dosyalar yüklenemedi.";
      setFeedback({ kind: "error", message });
      notify({ title: "Medya yüklenemedi", description: message, tone: "danger" });
    },
    onSettled: () => void client.invalidateQueries({ queryKey: ["media"] }),
  });
  const rows = query.data?.rows ?? [];
  const meta = query.data?.meta ?? normalizeListMeta(null, { page: list.page, pageSize: list.pageSize, rowCount: rows.length });
  const used = rows.filter((row) => row.usageCount > 0).length;

  return (
    <ListWorkspace
      className={styles.page}
        eyebrow="Varlık yönetimi"
        title="Medya kütüphanesi"
        description="Görselleri ve site dokümanlarını tek kaynaktan yönetin, kullanım yerlerini izleyin."
        actions={
          <>
            <input
              ref={input}
              hidden
              multiple
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif,image/gif,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,application/pdf"
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                if (files.length) upload.mutate(files);
                event.currentTarget.value = "";
              }}
            />
            <Button disabled={upload.isPending} onClick={() => input.current?.click()}>
              <Upload size={17} />
              {upload.isPending ? "Dosyalar yükleniyor…" : "Dosya yükle"}
            </Button>
          </>
        }
      >

      <SummaryStrip ariaLabel="Medya özeti" items={[
        { label: "Kütüphane", value: meta.total.toLocaleString("tr-TR"), description: "Aktif medya kaydı" },
        { label: "Bu sayfada kullanımda", value: used, description: "En az bir içeriğe bağlı" },
        { label: "Bu sayfada bağımsız", value: Math.max(0, rows.length - used), description: "Henüz içeriğe bağlanmamış" },
      ]} />

      {feedback && (
        <div className={`${styles.feedback} ${feedback.kind === "success" ? styles.feedbackSuccess : ""}`} role={feedback.kind === "error" ? "alert" : "status"}>
          <span>{feedback.message}</span>
          <Button size="sm" variant="ghost" onClick={() => setFeedback(null)}>Kapat</Button>
        </div>
      )}

      <div className={styles.mediaToolbar}>
        <div className={styles.mediaFilters}>
          <label className={styles.mediaSearch}>
            <Search size={17} aria-hidden="true" />
            <span className="sr-only">Medya ara</span>
            <Input value={list.searchInput} onChange={(event) => list.setSearchInput(event.target.value)} placeholder="Dosya adına göre ara…" type="search" />
          </label>
          <Select className={styles.mediaSelect} value={list.filters.kind} onChange={(event) => list.setFilter("kind", event.target.value)} aria-label="Dosya türü">
            <option value="">Tüm dosyalar</option>
            <option value="image">Görseller</option>
            <option value="document">Dokümanlar</option>
          </Select>
          {list.activeFilterCount > 0 && <Button variant="ghost" onClick={list.clear}>Filtreleri temizle</Button>}
        </div>
      </div>

      {query.isLoading ? (
        <div className={styles.assetGrid}>{Array.from({ length: 8 }, (_, index) => <Skeleton key={index} lines={3} />)}</div>
      ) : query.isError ? (
        <ErrorState title="Medya kütüphanesi yüklenemedi" description="Kayıtları yeniden almak için tekrar deneyin." onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Bu görünümde medya yok"
          description="Filtreleri temizleyin veya kütüphaneye yeni bir dosya ekleyin."
          action={<Button onClick={() => input.current?.click()}><Upload size={16} />Dosya yükle</Button>}
        />
      ) : (
        <>
          <section className={styles.assetGrid} aria-label="Medya kayıtları">
            {rows.map((asset) => (
              <Link className={styles.assetCard} to={`/medya/${asset.id}`} key={asset.id}>
                <div className={styles.assetPreview}>
                  <span className={styles.assetType}>{asset.kind === "image" ? "Görsel" : "Doküman"}</span>
                  {asset.kind === "image" && asset.url ? <img src={asset.url} alt="" loading="lazy" /> : asset.kind === "document" ? <FileText aria-hidden="true" /> : <ImagePlus aria-hidden="true" />}
                </div>
                <div className={styles.assetBody}>
                  <strong title={asset.name}>{asset.name}</strong>
                  <span>{formatBytes(asset.size)} · {asset.createdAt.toLocaleDateString("tr-TR")}</span>
                  <span className={styles.assetUsage}>{asset.usageCount} kullanım</span>
                </div>
              </Link>
            ))}
          </section>
          <footer className={styles.pager}>
            <p><strong>{meta.total.toLocaleString("tr-TR")}</strong> kayıttan bu sayfada {rows.length} kayıt gösteriliyor</p>
            <div className={styles.pagerActions}>
              <Select value={list.pageSize} onChange={(event) => list.setPageSize(Number(event.target.value))} aria-label="Sayfa başına kayıt">
                <option value={25}>25 / sayfa</option>
                <option value={50}>50 / sayfa</option>
                <option value={100}>100 / sayfa</option>
              </Select>
              <Button size="sm" variant="secondary" disabled={list.page <= 1} onClick={() => list.setPage(list.page - 1)}>Önceki</Button>
              <span>{meta.pages ? `${list.page} / ${meta.pages}` : "0 / 0"}</span>
              <Button size="sm" variant="secondary" disabled={!meta.pages || list.page >= meta.pages} onClick={() => list.setPage(list.page + 1)}>Sonraki</Button>
            </div>
          </footer>
        </>
      )}
    </ListWorkspace>
  );
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
